import { useCallback } from 'react';
import { apiClient, getAccessToken, ApiRequestError } from '../api/client';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export interface ImportCellError {
  column: string;
  message: string;
}

export interface ImportRow {
  row: number;
  status: 'ok' | 'error';
  label: string | null;
  catalog_key: string | null;
  court_label: string | null;
  url: string | null;
  groups: string[];
  holder?: string | null;
  holder_new?: boolean;
  has_totp: boolean;
  errors: ImportCellError[];
  warnings: string[];
}

export interface ImportReport {
  dry_run: boolean;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  created: number;
  created_ids: string[];
  rows: ImportRow[];
}

/**
 * fetch com o token do painel. O apiClient só envia/recebe JSON; aqui é preciso
 * enviar um arquivo e baixar um .xlsx. Se o token tiver expirado (401), uma chamada
 * qualquer pelo apiClient renova o token (ou leva ao login) e a requisição é refeita.
 */
async function fetchWithAuth(endpoint: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const token = getAccessToken();
    return fetch(`${API_BASE}${endpoint}`, {
      ...init,
      headers: { ...(init.headers as Record<string, string>), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  };
  let response = await send();
  if (response.status === 401) {
    await apiClient.get('/users/me/membership');
    response = await send();
  }
  return response;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') return body.detail;
  } catch {
    // corpo não é JSON: usa a mensagem genérica abaixo
  }
  return `Erro ${response.status} ao processar a planilha.`;
}

export function useCredentialImport() {
  const downloadTemplate = useCallback(async () => {
    const response = await fetchWithAuth('/credentials/import/template');
    if (!response.ok) {
      throw new ApiRequestError(response.status, await errorMessage(response));
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo-importacao-lictor.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);

  /** dryRun=true: só a prévia, nada é gravado. dryRun=false: grava tudo ou nada. */
  const importFile = useCallback(async (file: File, dryRun: boolean): Promise<ImportReport> => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetchWithAuth(`/credentials/import?dry_run=${dryRun}`, { method: 'POST', body });
    if (response.ok) {
      return response.json();
    }
    if (response.status === 422) {
      // Linhas com erro: o backend devolve o mesmo relatório da prévia (nada foi gravado).
      const report = await response.clone().json().catch(() => null);
      if (report && Array.isArray(report.rows)) return report as ImportReport;
    }
    throw new ApiRequestError(response.status, await errorMessage(response));
  }, []);

  return { downloadTemplate, importFile };
}
